import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useQuery } from '@tanstack/react-query';
import { useState, useContext, useMemo, useEffect } from 'react';
import { Button, Icon, InlineNotification, Input, Select, Spinner, Table, Tooltip, useLocalizeMessage, useToastNotification, } from 'libs.nucleus';
import { ApiClientService, AuthContext, useApiClient } from 'libs.react_internal';
import { DataBuilderSettingsModal } from './data_builder_settings_modal.component';
import { DataTransferTemplateContext } from '../../../contexts/data_transfer_template/data_transfer_template.context';
import { StudiesContext } from '../../../contexts/studies/studies.context';
import { getCubeApi } from '../../hooks/use_cube_api/use_cube_api.hook';
export const CreateTransferTemplateByDataBuilderStep3 = () => {
    const translate = useLocalizeMessage();
    const toast = useToastNotification();
    const { entityId } = useContext(AuthContext);
    const momClient = useApiClient(ApiClientService.MOM);
    const webClient = useApiClient(ApiClientService.WEB);
    const { createTemplateFormData, setCreateTemplateFormData, setIsCreateFormValid, isReadOnly } = useContext(DataTransferTemplateContext);
    const defaultStudyOption = {
        label: translate('Select a study'),
        value: '',
    };
    const defaultEnvOption = {
        label: translate('Select an environment'),
        value: '',
    };
    const { studies } = useContext(StudiesContext);
    const [isInitialized, setIsInitialized] = useState(false);
    const [selectedStudy, setSelectedStudy] = useState(defaultStudyOption);
    const [selectedEnv, setSelectedEnv] = useState(defaultEnvOption);
    const [isLoading, setIsLoading] = useState(false);
    const [queryResult, setQueryResult] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [columns, setColumns] = useState([]);
    const [selectedDataView, setSelectedDataView] = useState({
        label: translate('Select a dataset'),
        value: '',
    });
    const [isDataBuilderSettingsModalOpen, setIsDataBuilderSettingsModalOpen] = useState(false);
    const [orderSettings, setOrderSettings] = useState({});
    const [outputArrangement, setOutputArrangement] = useState({});
    const [currentLimit, setCurrentLimit] = useState(100);
    // Add new filter state
    const [filterSettings, setFilterSettings] = useState([]);
    const [dimensionValues, setDimensionValues] = useState({});
    const initializeSelectedFields = (views) => {
        const query = createTemplateFormData?.jsonQuery?.query;
        if (!query) {
            return { fields: new Set(), order: [] };
        }
        const selectedView = views.find((view) => view.name === createTemplateFormData?.viewName);
        if (!selectedView) {
            return { fields: new Set(), order: [] };
        }
        const dimensions = query?.dimensions?.map((dim) => {
            const fieldData = selectedView.dimensions.find((d) => d.name === dim);
            return {
                id: dim,
                type: 'dimension',
                shortTitle: fieldData?.shortTitle || dim,
            };
        }) || [];
        const measures = query?.measures?.map((measure) => {
            const fieldData = selectedView.measures.find((m) => m.name === measure);
            return {
                id: measure,
                type: 'measure',
                shortTitle: fieldData?.shortTitle || measure,
            };
        }) || [];
        const allFields = [...dimensions, ...measures];
        const order = allFields.map((field) => ({ id: field.id, type: field.type }));
        return { fields: new Set(allFields), order };
    };
    const [selectedDataFields, setSelectedDataFields] = useState(new Set());
    const [selectionOrder, setSelectionOrder] = useState([]);
    const [didQueryRun, setDidQueryRun] = useState(false);
    const studyOptions = studies.map((study) => ({
        label: study.name,
        value: study.id.toString(),
    }));
    const fetchEnvs = async () => {
        try {
            setSelectedEnv(defaultEnvOption);
            const [devEnvironmentsResponse] = await Promise.all([
                momClient.get(`/v1/entities/${entityId}/studies/${selectedStudy.value}/environments`, {
                    params: {
                        clusterTypes: 'dev',
                        withCortexAccess: true,
                    },
                }),
            ]);
            return [...devEnvironmentsResponse.data.data.filter((env) => !env.deployment.isProd)];
        }
        catch (error) {
            toast.addNotification({
                type: 'error',
                title: error.message ||
                    error.error.message ||
                    translate('An error occurred while fetching environments. Please try again.'),
            });
        }
    };
    const { data: environments = [], isLoading: isFetchingEnvs } = useQuery({
        queryKey: ['entities', entityId, 'studies', selectedStudy.value, 'dev-with-cortex-access'],
        queryFn: fetchEnvs,
        enabled: !!entityId && !!selectedStudy.value,
        refetchOnWindowFocus: false,
    });
    const envOptions = environments.map((env) => ({
        label: env.name,
        value: env.cortexId,
    }));
    const fetchDataTransferViews = async () => {
        const { data } = await webClient.post('/api/data_transfer/views/list');
        return data;
    };
    const { data: dataTransferViews = [], isLoading: isFetchingDataTransferViews } = useQuery({
        queryKey: ['entities', entityId, 'data-transfer', 'views'],
        queryFn: fetchDataTransferViews,
        enabled: !!entityId,
        refetchOnWindowFocus: false,
        refetchOnMount: 'always',
    });
    // Keep selectedDataFields in sync with context data during navigation
    useEffect(() => {
        const viewsArray = Array.isArray(dataTransferViews) ? dataTransferViews : dataTransferViews?.data || [];
        if (viewsArray.length === 0) {
            return;
        }
        const { fields, order } = initializeSelectedFields(viewsArray);
        setSelectedDataFields(fields);
        setSelectionOrder(order);
        // Update form validity based on whether fields are selected
        setIsCreateFormValid(fields.size > 0);
    }, [JSON.stringify(createTemplateFormData.jsonQuery), createTemplateFormData.viewName, dataTransferViews]);
    // Initialize outputArrangement and orderSettings from existing template data when editing
    useEffect(() => {
        // Initialize outputArrangement from existing template data
        if (createTemplateFormData.outputArrangement && Object.keys(createTemplateFormData.outputArrangement).length > 0) {
            setOutputArrangement(createTemplateFormData.outputArrangement);
        }
        // Initialize orderSettings, limit, and filters from existing query data
        const query = createTemplateFormData?.jsonQuery?.query;
        if (query?.order && Object.keys(query.order).length > 0) {
            setOrderSettings(query.order);
        }
        if (query?.limit !== undefined) {
            setCurrentLimit(query.limit);
        }
        // Initialize filters from existing query data
        if (query?.filters && Array.isArray(query.filters) && query.filters.length > 0) {
            const loadedFilters = query.filters.map((filter) => ({
                field: filter.member,
                operator: filter.operator,
                value: Array.isArray(filter.values) ? filter.values : [filter.values].filter(Boolean),
            }));
            setFilterSettings(loadedFilters);
        }
    }, [createTemplateFormData.outputArrangement, JSON.stringify(createTemplateFormData.jsonQuery)]);
    const dataViewOptions = useMemo(() => {
        const viewsArray = Array.isArray(dataTransferViews) ? dataTransferViews : dataTransferViews?.data || [];
        return viewsArray.map((view) => ({
            label: view.title,
            value: view.name,
        }));
    }, [dataTransferViews]);
    useEffect(() => {
        if (isInitialized) {
            return;
        }
        if (dataViewOptions.length > 0) {
            // Always try to find the view from createTemplateFormData first (for editing existing templates)
            const foundViewObj = dataViewOptions.find((view) => view.value === createTemplateFormData?.viewName);
            if (foundViewObj) {
                // If we found the view from the template data, use it
                setSelectedDataView(foundViewObj);
                setCreateTemplateFormData({
                    ...createTemplateFormData,
                    viewName: foundViewObj.value,
                });
            }
            else {
                // Fallback to the first available view if we can't find the one from template data
                setSelectedDataView(dataViewOptions[0]);
                setCreateTemplateFormData({
                    ...createTemplateFormData,
                    viewName: dataViewOptions[0].value,
                });
            }
            setIsInitialized(true);
        }
    }, [dataViewOptions, selectedDataFields]);
    useEffect(() => {
        // Create columns based on output arrangement or selection order
        const fieldsArray = Array.from(selectedDataFields);
        if (Object.keys(outputArrangement).length > 0) {
            // Use output arrangement for ordering and naming
            const arrangedFields = fieldsArray
                .filter((field) => outputArrangement[field.id]) // Only include fields that have arrangement data
                .sort((a, b) => outputArrangement[a.id].position - outputArrangement[b.id].position);
            const orderedColumns = arrangedFields.map((field) => ({
                header: outputArrangement[field.id].alias,
                accessorFn: (row) => row[field.id],
            }));
            setColumns(orderedColumns);
        }
        else {
            // Fallback to original selection order
            const orderedColumns = selectionOrder.map((orderItem) => {
                const field = fieldsArray.find((f) => f.id === orderItem.id && f.type === orderItem.type);
                return {
                    header: field?.shortTitle || orderItem.id,
                    accessorFn: (row) => row[orderItem.id],
                };
            });
            setColumns(orderedColumns);
        }
    }, [selectedDataFields, selectionOrder, outputArrangement]);
    const filteredDataFields = useMemo(() => {
        const viewsArray = Array.isArray(dataTransferViews) ? dataTransferViews : dataTransferViews?.data || [];
        const selectedView = viewsArray.find((view) => view.name === selectedDataView.value);
        if (!selectedView) {
            return [];
        }
        const dimensionsWithType = selectedView.dimensions.map((dimension) => ({
            ...dimension,
            fieldType: 'dimension',
        }));
        const measuresWithType = selectedView.measures.map((measure) => ({
            ...measure,
            fieldType: 'measure',
        }));
        const allFields = [...dimensionsWithType, ...measuresWithType];
        return allFields.filter((field) => field.title.toLowerCase().includes(searchTerm.toLowerCase()));
    }, [dataTransferViews, searchTerm, selectedDataView]);
    const selectedFieldsLookup = useMemo(() => new Set(Array.from(selectedDataFields).map((f) => `${f.id}:${f.type}`)), [selectedDataFields]);
    const handleDataViewChange = (option) => {
        setSelectedDataView(option);
        setSelectedDataFields(new Set());
        setSelectionOrder([]);
        setOrderSettings({}); // Reset order settings when dataset changes
        setOutputArrangement({}); // Reset output arrangement when dataset changes
        setCreateTemplateFormData({
            ...createTemplateFormData,
            viewName: option.value,
            jsonQuery: { query: { dimensions: [], measures: [] } }, // Clear query data when dataset changes
            outputArrangement: {}, // Clear output arrangement in form data too
        });
        setIsCreateFormValid(false); // Reset form validity since no fields are selected
    };
    const handleDataFieldSelect = (fieldId, fieldType, fieldShortTitle) => {
        const newSelected = new Set(selectedDataFields);
        const newOrder = [...selectionOrder];
        const fieldObj = { id: fieldId, type: fieldType, shortTitle: fieldShortTitle };
        const existingField = Array.from(newSelected).find((field) => field.id === fieldId && field.type === fieldType);
        if (existingField) {
            newSelected.delete(existingField);
            // Remove from selection order
            const orderIndex = newOrder.findIndex((item) => item.id === fieldId && item.type === fieldType);
            if (orderIndex !== -1) {
                newOrder.splice(orderIndex, 1);
            }
        }
        else {
            newSelected.add(fieldObj);
            // Add to selection order
            newOrder.push({ id: fieldId, type: fieldType });
        }
        setSelectedDataFields(newSelected);
        setSelectionOrder(newOrder);
        setIsCreateFormValid(newSelected.size > 0);
        // Update outputArrangement to include any new fields and remove deselected ones
        let updatedOutputArrangement = outputArrangement;
        if (Object.keys(outputArrangement).length > 0) {
            const newOutputArrangement = {};
            const fieldsArray = Array.from(newSelected);
            // Keep existing arrangements for fields that are still selected
            fieldsArray.forEach((field) => {
                if (outputArrangement[field.id]) {
                    newOutputArrangement[field.id] = outputArrangement[field.id];
                }
            });
            // Add new fields at the end
            const existingCount = Object.keys(newOutputArrangement).length;
            const maxPosition = existingCount > 0 ? Math.max(...Object.values(newOutputArrangement).map((item) => item.position)) : 0;
            let newFieldPosition = maxPosition + 1;
            fieldsArray.forEach((field) => {
                if (!newOutputArrangement[field.id]) {
                    newOutputArrangement[field.id] = {
                        alias: field.shortTitle,
                        position: newFieldPosition++,
                    };
                }
            });
            updatedOutputArrangement = newOutputArrangement;
            setOutputArrangement(updatedOutputArrangement);
        }
        setCreateTemplateFormData({
            ...createTemplateFormData,
            jsonQuery: { query: prepareQuery(newSelected, orderSettings, currentLimit) },
            outputArrangement: updatedOutputArrangement,
        });
    };
    const prepareQuery = (selectedDataFields, orderSettings, limit, filters) => {
        const fieldsArray = Array.from(selectedDataFields);
        const query = {
            dimensions: fieldsArray.filter((field) => field.type === 'dimension').map((field) => field.id),
            measures: fieldsArray.filter((field) => field.type === 'measure').map((field) => field.id),
        };
        // Add order configuration if provided
        if (orderSettings && Object.keys(orderSettings).length > 0) {
            query.order = orderSettings;
        }
        // Add limit if provided
        if (limit !== undefined) {
            query.limit = limit;
        }
        // Add filters if provided
        if (filters && filters.length > 0) {
            query.filters = filters.map((filter) => {
                const { operator } = filter;
                return {
                    member: filter.field,
                    operator,
                    values: filter.value,
                };
            });
        }
        return query;
    };
    const handleApplySettings = (settings) => {
        // Update settings state
        setCurrentLimit(settings.limit);
        setOrderSettings(settings.order);
        setOutputArrangement(settings.outputArrangement);
        setFilterSettings(settings.filters);
        // Update the form data with the new query including settings
        setCreateTemplateFormData({
            ...createTemplateFormData,
            jsonQuery: { query: prepareQuery(selectedDataFields, settings.order, settings.limit, settings.filters) },
            outputArrangement: settings.outputArrangement,
        });
        // Close the modal
        setIsDataBuilderSettingsModalOpen(false);
        // Auto-reload preview with the new settings if we have selected fields
        if (selectedDataFields.size > 0) {
            handleRunQueryWithSettings(settings);
        }
        console.log('Applied settings:', settings);
    };
    const handleRunQueryWithSettings = async (settings) => {
        try {
            // First perform frontend validation
            if (selectedDataFields.size === 0) {
                toast.addNotification({
                    type: 'error',
                    title: translate('Please select at least one data field before running a query'),
                });
                return;
            }
            setIsLoading(true);
            setQueryResult([]);
            const cubeApi = getCubeApi({
                entityId: entityId.toString(),
                studyId: selectedStudy.value,
                activeReportsTab: 0,
                selectedTestEnv: selectedEnv,
                prodOrgs: [],
            });
            // Prepare query with the new settings
            const backendQuery = prepareQuery(selectedDataFields, settings.order, settings.limit, settings.filters);
            // ... rest of the query execution logic from handleRunQuery
            const result = await cubeApi.load(backendQuery, {
                subscribe: true,
            });
            setQueryResult(result?.tablePivot() ?? []);
            setIsLoading(false);
        }
        catch (error) {
            console.error('Error running query:', error);
            setIsLoading(false);
            toast.addNotification({
                type: 'error',
                title: translate('Error running query'),
            });
        }
    };
    const handleRunQuery = async () => {
        try {
            // First perform frontend validation
            if (selectedDataFields.size === 0) {
                toast.addNotification({
                    type: 'error',
                    title: translate('Please select at least one data field before running a query'),
                });
                return;
            }
            setIsLoading(true);
            setQueryResult([]);
            const cubeApi = getCubeApi({
                entityId: entityId.toString(),
                studyId: selectedStudy.value,
                activeReportsTab: 0,
                selectedTestEnv: selectedEnv,
                prodOrgs: [],
            });
            // Prepare query with filters for backend
            const backendQuery = prepareQuery(selectedDataFields, orderSettings, currentLimit, filterSettings);
            const response = await cubeApi.load(backendQuery, {
                subscribe: true,
            });
            // Get raw columns from backend response
            const rawColumns = response?.tableColumns() || [];
            // Apply outputArrangement transformations if configured
            let processedColumns;
            if (Object.keys(outputArrangement).length > 0) {
                // Transform columns using outputArrangement (aliases and positions)
                const columnMap = new Map(rawColumns.map((col) => [col.dataIndex, col]));
                // Create array of fields with their arrangements, sorted by position
                const arrangedFields = Object.entries(outputArrangement)
                    .map(([fieldId, arrangement]) => ({
                    fieldId,
                    ...arrangement,
                    originalColumn: columnMap.get(fieldId),
                }))
                    .filter((field) => field.originalColumn) // Only include fields that exist in response
                    .sort((a, b) => a.position - b.position);
                processedColumns = arrangedFields.map((field) => ({
                    header: field.alias,
                    accessorFn: (row) => row[field.fieldId],
                }));
            }
            else {
                // Fallback to original column structure
                processedColumns = rawColumns.map((column) => ({
                    header: column.shortTitle,
                    accessorFn: (row) => row[column.dataIndex],
                }));
            }
            setColumns(processedColumns);
            setQueryResult(response?.tablePivot() ?? []);
        }
        catch (error) {
            const axiosError = error;
            const errorMessage = axiosError.response?.data?.message ||
                translate('An error occurred while running the query. Please try again.');
            toast.addNotification({
                type: 'error',
                title: errorMessage,
            });
        }
        finally {
            setIsLoading(false);
            setDidQueryRun(true);
        }
    };
    // Add function to fetch dimension values dynamically
    const fetchDimensionValues = async (dimensionName) => {
        if (!selectedStudy.value || !selectedEnv.value) {
            return [];
        }
        try {
            const cubeApi = getCubeApi({
                entityId: entityId.toString(),
                studyId: selectedStudy.value,
                activeReportsTab: 0,
                selectedTestEnv: selectedEnv,
                prodOrgs: [],
            });
            const query = { dimensions: [dimensionName], measures: [] };
            const response = await cubeApi.load(query, { subscribe: true });
            const data = response?.tablePivot() || [];
            let uniqueValues = [];
            if (data.length > 0) {
                if (data[0] && data[0][dimensionName] !== undefined) {
                    uniqueValues = [...new Set(data.map((row) => row[dimensionName]).filter(Boolean))];
                }
                else {
                    const firstRow = data[0];
                    if (firstRow) {
                        const availableKeys = Object.keys(firstRow);
                        const matchingKey = availableKeys.find((key) => key.includes(dimensionName.split('.').pop() || ''));
                        if (matchingKey) {
                            uniqueValues = [...new Set(data.map((row) => row[matchingKey]).filter(Boolean))];
                        }
                    }
                }
            }
            return uniqueValues;
        }
        catch (error) {
            console.error(`Error fetching values for dimension ${dimensionName}:`, error);
            return [];
        }
    };
    // Function to get dimension values (with caching)
    const getDimensionValues = async (dimensionName) => {
        // Check if we already have the values cached
        if (dimensionValues[dimensionName]) {
            return dimensionValues[dimensionName];
        }
        // Fetch new values
        const values = await fetchDimensionValues(dimensionName);
        // Cache the values
        setDimensionValues((prev) => ({
            ...prev,
            [dimensionName]: values,
        }));
        return values;
    };
    return (_jsxs("div", { className: 'grid grid-cols-2 lg:grid-cols-[1.5fr_2fr] gap-4 size-full', children: [_jsxs("div", { className: 'flex flex-col space-y-4 min-w-0', children: [_jsxs("div", { className: 'flex justify-between items-center', children: [_jsx("div", { children: translate('Data selector') }), _jsx(Button, { disabled: !selectedEnv.value || isLoading, label: translate('Run'), onClick: handleRunQuery })] }), _jsxs("div", { className: 'grid grid-cols-2 gap-4 border-b border-gray-100 pb-4 mb-4', children: [_jsx(Select, { placeholder: defaultStudyOption.label, options: studyOptions, value: selectedStudy, onChange: (option) => setSelectedStudy(option), width: 'full', searchableOptions: true }), _jsx(Select, { disabled: isFetchingEnvs || !selectedStudy.value, placeholder: defaultEnvOption.label, options: envOptions, value: selectedEnv, onChange: (option) => setSelectedEnv(option), width: 'full', searchableOptions: true })] }), _jsxs("div", { className: 'grid grid-cols-2 gap-4', children: [_jsx(Input, { id: 'search-data-fields', placeholder: translate('Search...'), value: searchTerm, onChange: (e) => setSearchTerm(e.target.value), width: 'full' }), _jsx(Select, { placeholder: selectedDataView.label, options: dataViewOptions, value: selectedDataView, onChange: handleDataViewChange, width: 'full', disabled: isFetchingDataTransferViews || isReadOnly })] }), _jsx("div", { children: _jsx(InlineNotification, { subtitle: translate('Changing the dataset will reset selected data fields'), title: '', type: 'info' }) }), _jsx("div", { className: 'flex-1 overflow-y-auto max-h-[500px]', "data-testid": 'data-builder-field-list', children: isFetchingDataTransferViews ? (_jsx("div", { className: 'flex items-center justify-center h-full', children: _jsx(Spinner, {}) })) : filteredDataFields && filteredDataFields.length > 0 ? (filteredDataFields.map((field) => {
                            const isSelected = selectedFieldsLookup.has(`${field.name}:${field.fieldType}`);
                            return (_jsxs("div", { "data-testid": 'data-builder-field-row', "data-field-id": field.name, "data-field-type": field.fieldType, className: `grid grid-cols-[1fr_auto_auto] items-center gap-3 py-3 px-4 border-b border-border-primary last:border-b-0 ${isReadOnly ? 'cursor-default' : 'cursor-pointer'} ${isSelected ? 'bg-blue-50 hover:bg-blue-100' : 'hover:bg-gray-50'}`, onClick: isReadOnly
                                    ? undefined
                                    : () => handleDataFieldSelect(field.name, field.fieldType, field.shortTitle), children: [_jsxs("div", { className: 'min-w-0', children: [_jsx("div", { children: field.shortTitle }), _jsx("div", { className: 'text-xs text-text-secondary', children: field.fieldType.charAt(0).toUpperCase() + field.fieldType.slice(1) })] }), _jsx("div", { className: 'text-text-secondary', children: field.type.charAt(0).toUpperCase() + field.type.slice(1) }), _jsx("div", { children: _jsx("div", { className: 'flex items-center justify-center', children: _jsx(Tooltip, { title: field.description, children: _jsx(Icon, { name: 'info' }) }) }) })] }, field.name));
                        })) : (_jsx("div", { className: 'flex items-center justify-center h-full', children: _jsx("div", { className: 'text-center text-gray-500', children: translate('No dimensions or measures found, try selecting a different dataset.') }) })) })] }), _jsxs("div", { className: 'border-l px-4 space-y-4 min-w-0 min-h-0 flex flex-col', "data-testid": 'data-builder-data-preview', children: [_jsxs("div", { className: 'flex justify-between items-center', children: [_jsx("div", { children: translate('Data preview') }), _jsx(Button, { label: translate('Settings'), onClick: () => setIsDataBuilderSettingsModalOpen(true), variant: 'secondary', disabled: selectedDataFields.size === 0 || isLoading })] }), _jsx("div", { className: 'pb-4 flex-1 min-h-0 overflow-y-auto', children: _jsx(Table, { columns: columns, data: queryResult, isLoading: isLoading, labelNoResults: didQueryRun
                                ? translate('No data available for the selected dimensions or measures.')
                                : translate('No data available, try running a query first.') }) })] }), _jsx(DataBuilderSettingsModal, { isOpen: isDataBuilderSettingsModalOpen, onClose: () => setIsDataBuilderSettingsModalOpen(false), onApply: handleApplySettings, selectedDataFields: Array.from(selectedDataFields).map((field) => ({
                    id: field.id,
                    type: field.type,
                    shortTitle: field.shortTitle,
                })), currentOrderSettings: orderSettings, currentOutputArrangement: outputArrangement, currentLimit: currentLimit, currentFilterSettings: filterSettings, selectedDataView: selectedDataView, dataTransferViews: Array.isArray(dataTransferViews) ? dataTransferViews : dataTransferViews?.data || [], getDimensionValues: getDimensionValues, readOnly: isReadOnly })] }));
};
